import { useCallback, useEffect, useMemo, useState } from 'react';
import supabase from '../../supabase/supabase';
import { CHAT_TABLE, MODERATION_TABLE } from '../Chat/chatConstants';

export default function useAdminModeration(session) {
  const [messages, setMessages] = useState([]);
  const [restrictions, setRestrictions] = useState([]);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    const [messageResult, moderationResult] = await Promise.all([
      supabase
        .from(CHAT_TABLE)
        .select('*')
        .eq('is_deleted', false)
        .order('created_at', { ascending: false })
        .limit(200),
      supabase
        .from(MODERATION_TABLE)
        .select('*')
        .order('created_at', { ascending: false })
    ]);

    if (messageResult.error || moderationResult.error) {
      setError('No fue posible cargar la información administrativa.');
      return;
    }

    setMessages(messageResult.data || []);
    setRestrictions(moderationResult.data || []);
    setError('');
  }, []);

  useEffect(() => {
    refresh();

    const channel = supabase
      .channel('admin_moderation_dashboard')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: CHAT_TABLE },
        refresh
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: MODERATION_TABLE },
        refresh
      )
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [refresh]);

  const users = useMemo(() => {
    const grouped = new Map();
    messages.forEach((message) => {
      const key = message.sender_id || `legacy-${message.nickname}`;
      const current = grouped.get(key);
      grouped.set(key, {
        nickname: message.nickname,
        sender_id: message.sender_id || '',
        messageCount: (current?.messageCount || 0) + 1
      });
    });

    return [...grouped.values()].sort((a, b) => (
      b.messageCount - a.messageCount
    ));
  }, [messages]);

  const moderate = async (user, action) => {
    const reason = window.prompt(
      `Motivo para ${action === 'ban' ? 'bloquear' : 'expulsar'} a ${user.nickname}:`,
      'Incumplimiento de las normas del chat'
    );
    if (reason === null) return;

    const expiresAt = action === 'expel'
      ? new Date(Date.now() + 15 * 60 * 1000).toISOString()
      : null;

    const { error: actionError } = await supabase
      .from(MODERATION_TABLE)
      .insert({
        subject_client_id: user.sender_id,
        subject_nickname: user.nickname,
        action,
        reason: reason.trim(),
        expires_at: expiresAt,
        created_by: session.user.id
      });

    if (actionError) setError('No se pudo aplicar la acción.');
    else refresh();
  };

  const restore = async (restriction) => {
    const { error: restoreError } = await supabase
      .from(MODERATION_TABLE)
      .update({ active: false, updated_at: new Date().toISOString() })
      .eq('id', restriction.id);

    if (restoreError) setError('No se pudo restablecer al participante.');
    else refresh();
  };

  const removeMessage = async (message) => {
    const confirmed = window.confirm(
      `¿Ocultar el mensaje de ${message.nickname}?`
    );
    if (!confirmed) return;

    const { error: removeError } = await supabase
      .from(CHAT_TABLE)
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: session.user.id,
        moderation_reason: 'Ocultado desde el panel administrativo'
      })
      .eq('id', message.id);

    if (removeError) setError('No se pudo ocultar el mensaje.');
    else refresh();
  };

  const clearAllMessages = async () => {
    const confirmed = window.confirm(
      '¿Estás seguro de que deseas eliminar TODOS los mensajes del chat? Esta acción ocultará todo el historial actual para los espectadores y no se puede deshacer.'
    );
    if (!confirmed) return;

    const { error: removeError } = await supabase
      .from(CHAT_TABLE)
      .update({
        is_deleted: true,
        deleted_at: new Date().toISOString(),
        deleted_by: session.user.id,
        moderation_reason: 'Limpieza masiva desde el panel administrativo'
      })
      .eq('is_deleted', false);

    if (removeError) setError('No se pudo limpiar el chat.');
    else refresh();
  };

  return {
    messages,
    users,
    restrictions,
    error,
    moderate,
    restore,
    removeMessage,
    clearAllMessages
  };
}